/**
 * URL guards shared by the Play hero and the cards split out of it.
 *
 * These live in their own module rather than in CockpitHero so the extracted
 * card components can use them without importing from their own parent (which
 * would be a runtime import cycle — type-only imports are erased, these are
 * not).
 */

/**
 * Guard for href values to prevent `javascript:` / `data:` / `vbscript:`
 * URIs from sneaking through caller-supplied props. These widgets expose
 * `href` on a public interface — without this guard, any future caller
 * passing untrusted data could plant an XSS via a malicious anchor target.
 * Allows only relative paths, fragments, and explicit http(s) origins.
 */
export const isSafeHref = (href: string | undefined): boolean => {
    if (!href) return false;
    /*
     * Reject tab/newline/carriage-return outright. The URL parser STRIPS these
     * from anywhere in a URL before resolving it, so `"/\t/evil.com"` passes a
     * naive first-two-character check and then collapses to `//evil.com` at
     * click time — defeating the protocol-relative guard below. Checking a
     * prefix is only sound once the string can no longer shrink.
     */
    if ((/[\t\n\r]/).test(href)) return false;
    /*
     * Reject protocol-relative and backslash-prefixed hrefs BEFORE the relative
     * check. `//evil.com` starts with "/" but browsers resolve it against the
     * current scheme and navigate straight off-site, which is the exact escape
     * this guard exists to close. Browsers also normalise `\` to `/`, so `\\host`
     * and `/\host` are the same trick wearing a different hat.
     */
    if ((/^[/\\]{2}/).test(href) || (/^\/\\/).test(href)) return false;
    if (href.startsWith("/") || href.startsWith("#")) return true;
    return (/^https?:\/\//i).test(href);
};

/**
 * Escape an arbitrary URL string for inline `background-image: url("…")`.
 * Replaces the double-quote that would otherwise close our quoted url(),
 * dropping the CSS-injection vector when the URL comes from external
 * data (e.g. CDN-resolved game art set on the model by upstream tooling).
 */
export const safeCssUrl = (url: string): string => `url("${url.replace(/"/g, "%22")}")`;
